import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Layout } from './components/Layout';
import { Home } from './pages/Home';
import { Loader2 } from 'lucide-react';

const Services = lazy(() => import('./pages/Services').then(({ Services: page }) => ({ default: page })));
const Demos = lazy(() => import('./pages/Demos').then(({ Demos: page }) => ({ default: page })));
const Pricing = lazy(() => import('./pages/Pricing').then(({ Pricing: page }) => ({ default: page })));
const FreeDemo = lazy(() => import('./pages/FreeDemo').then(({ FreeDemo: page }) => ({ default: page })));
const BookDemo = lazy(() => import('./pages/BookDemo').then(({ BookDemo: page }) => ({ default: page })));
const RemodelingWebsites = lazy(() => import('./pages/RemodelingWebsites').then(({ RemodelingWebsites: page }) => ({ default: page })));
const About = lazy(() => import('./pages/About').then(({ About: page }) => ({ default: page })));
const WebDesign = lazy(() => import('./pages/services/WebDesign').then(({ WebDesign: page }) => ({ default: page })));
const Hosting = lazy(() => import('./pages/services/Hosting').then(({ Hosting: page }) => ({ default: page })));
const LocalSEO = lazy(() => import('./pages/services/LocalSEO').then(({ LocalSEO: page }) => ({ default: page })));
const Ads = lazy(() => import('./pages/services/Ads').then(({ Ads: page }) => ({ default: page })));
const Workflows = lazy(() => import('./pages/services/Workflows').then(({ Workflows: page }) => ({ default: page })));
const AIReceptionist = lazy(() => import('./pages/services/AIReceptionist').then(({ AIReceptionist: page }) => ({ default: page })));
const Blog = lazy(() => import('./pages/Blog').then(({ Blog: page }) => ({ default: page })));
const Privacy = lazy(() => import('./pages/Privacy').then(({ Privacy: page }) => ({ default: page })));
const Terms = lazy(() => import('./pages/Terms').then(({ Terms: page }) => ({ default: page })));
const NotFound = lazy(() => import('./pages/NotFound').then(({ NotFound: page }) => ({ default: page })));
const WhyWebsitesFail = lazy(() => import('./pages/blog/WhyWebsitesFail').then(({ WhyWebsitesFail: page }) => ({ default: page })));
const FiftyDollarWebsite = lazy(() => import('./pages/blog/FiftyDollarWebsite').then(({ FiftyDollarWebsite: page }) => ({ default: page })));
const MonthlyWebsiteValue = lazy(() => import('./pages/blog/MonthlyWebsiteValue').then(({ MonthlyWebsiteValue: page }) => ({ default: page })));
const SeattleContractorSEO = lazy(() => import('./pages/blog/SeattleContractorSEO').then(({ SeattleContractorSEO: page }) => ({ default: page })));
const FastContractorWebsiteSeattle = lazy(() => import('./pages/blog/FastContractorWebsiteSeattle').then(({ FastContractorWebsiteSeattle: page }) => ({ default: page })));
const SeattleLocalSEOServicesAreas = lazy(() => import('./pages/blog/SeattleLocalSEOServicesAreas').then(({ SeattleLocalSEOServicesAreas: page }) => ({ default: page })));
const ClientReadyAIWebsites = lazy(() => import('./pages/ClientReadyAIWebsites').then(({ ClientReadyAIWebsites: page }) => ({ default: page })));
const ClientReadyAIWebsitesSuccess = lazy(() => import('./pages/ClientReadyAIWebsitesSuccess').then(({ ClientReadyAIWebsitesSuccess: page }) => ({ default: page })));
const ClientReadyAIWebsitesTerms = lazy(() => import('./pages/ClientReadyAIWebsitesTerms').then(({ ClientReadyAIWebsitesTerms: page }) => ({ default: page })));
const Post1Carousel = lazy(() => import('./Post1Carousel.jsx'));
const Login = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })));
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const CheckoutSuccess = lazy(() => import('./pages/CheckoutSuccess').then(m => ({ default: m.CheckoutSuccess })));
const AuthCallback = lazy(() => import('./pages/AuthCallback').then(m => ({ default: m.AuthCallback })));
const ResetPassword = lazy(() => import('./pages/ResetPassword').then(m => ({ default: m.ResetPassword })));

function PageLoader() {
  return (
    <div data-page-loader className="min-h-[80vh] flex items-center justify-center" aria-busy="true" aria-label="Loading">
      <Loader2 className="w-10 h-10 text-verto-teal animate-spin" />
    </div>
  );
}
export function App() {
  useEffect(() => {
    // Used by vite-plugin-prerender to know when the app has rendered.
    document.dispatchEvent(new Event('render-event'));
  }, []);

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Layout>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/web-design" element={<WebDesign />} />
          <Route path="/services/hosting" element={<Hosting />} />
          <Route path="/services/local-seo" element={<LocalSEO />} />
          <Route path="/services/ads" element={<Ads />} />
          <Route path="/services/workflows" element={<Workflows />} />
          <Route path="/services/ai-receptionist" element={<AIReceptionist />} />
          <Route path="/demos" element={<Demos />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/free-demo" element={<FreeDemo />} />
          <Route path="/book-demo" element={<BookDemo />} />
          <Route path="/lp/remodeling-websites" element={<RemodelingWebsites />} />
          <Route path="/about" element={<About />} />
          <Route path="/lp/client-ready-ai-websites" element={<ClientReadyAIWebsites />} />
          <Route path="/lp/client-ready-ai-websites/success" element={<ClientReadyAIWebsitesSuccess />} />
          <Route path="/lp/client-ready-ai-websites/terms" element={<ClientReadyAIWebsitesTerms />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Login />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/checkout-success" element={<CheckoutSuccess />} />
          <Route path="/blog" element={<Blog />} />
          <Route
            path="/blog/why-local-business-websites-fail-to-get-customers"
            element={<WhyWebsitesFail />}
          />
          <Route
            path="/blog/50-dollar-month-website-vs-5000-website"
            element={<FiftyDollarWebsite />}
          />
          <Route
            path="/blog/monthly-website-checklist-for-local-business"
            element={<MonthlyWebsiteValue />}
          />

          <Route
            path="/blog/seattle-contractor-seo-not-showing-on-google"
            element={<SeattleContractorSEO />}
          />
          <Route
            path="/blog/fast-contractor-website-seattle-leads"
            element={<FastContractorWebsiteSeattle />}
          />
          <Route
            path="/blog/seattle-local-seo-service-areas-contractors"
            element={<SeattleLocalSEOServicesAreas />}
          />

          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="*" element={<NotFound />} />
          {import.meta.env.DEV && (
            <Route path="/post1-carousel" element={<Post1Carousel />} />
          )}

        </Routes>
        </Suspense>
      </Layout>
    </Router>);

}
